var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { WithIcon } from "../../_primitives/WithIcon.js";
import { parseQuiBaseProps } from "../../../utils/index.js";
/**
 * A primitive link component that provides consistent styles for three kinds of links: external hrefs, react-router links, and
 * non-navigation links (or "action links") that run a function on click.
 * */
export const QuiLink = (_a) => {
    var { className, iconLeft, iconRight, size, children } = _a, otherProps = __rest(_a, ["className", "iconLeft", "iconRight", "size", "children"]);
    const cn = classNames("qui-link", {
        [`qui-link-${otherProps.variant}`]: otherProps.variant,
        "qui-link-with-icon": !!iconRight || !!iconLeft,
    }, className);
    const linkTextClassName = classNames("qui-link-text", {
        [`qui-${size}`]: !!size,
    });
    // link text is wrapped in a span so that it can be underlined cleanly
    const linkContents = (React.createElement(WithIcon, { iconLeft: iconLeft, iconRight: iconRight, iconSize: textSizeToIconSize[size !== null && size !== void 0 ? size : "text-md"] },
        React.createElement("span", { className: linkTextClassName }, children)));
    /**
     * For non-navigation links, use a button component styled to look like a link (we do not want to use <a> tag here for a11y reasons)
     *
     * Note: It is kinda weird to make something look like a link, but not act like a link, you know? But sometimes that's what designers want.
     */
    if (!("to" in otherProps)) {
        return (React.createElement("button", Object.assign({}, parseQuiBaseProps(otherProps), { onClick: otherProps.onClick, type: "button", className: cn }), linkContents));
    }
    /**
     * For all other links, use either an <a> tag or a <Link> element from react-router-dom
     */
    // The shared props that can be passed to either <a> or <Link>
    const anchorProps = Object.assign({}, parseQuiBaseProps(otherProps));
    // Add the onClick prop if it exists
    if ("onClick" in otherProps) {
        anchorProps.onClick = otherProps.onClick;
    }
    // If external is true, add rel="noreferrer" and target="_blank"
    if ("external" in otherProps && otherProps.external) {
        anchorProps.rel = "noreferrer";
        anchorProps.target = "_blank";
    }
    // If download is defined, add the download attribute
    if ("download" in otherProps && otherProps.download) {
        anchorProps.download = otherProps.download;
    }
    // Download & external links render as regular <a> tags
    if ("download" in anchorProps || "target" in anchorProps) {
        return (React.createElement("a", Object.assign({}, anchorProps, { href: otherProps.to, className: cn }), linkContents));
    }
    // Everything else is a router link, so we use the react-router-dom <Link> component
    return (React.createElement(Link, Object.assign({}, anchorProps, { to: otherProps.to, className: cn }), linkContents));
};
const textSizeToIconSize = {
    "text-xs": "xs",
    "text-sm": "sm",
    "text-md": "md",
    "text-lg": "lg",
    "text-xl": "xl",
};
